import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios, { AxiosError, AxiosResponse } from 'axios';
// context
import { BookingConfigContext } from '../../../context/bookingConfigContext/bookingConfigContext';
import { BookingQuoteContext } from '../../../context/quoteContext/quoteContext';
import { BookingContext } from '../../../context/bookingContext/bookingContext';
import { BookingDataContext } from '../../../context/bookingDataContext/bookingDataContext';
import { NotificationContext } from '../../../shared/components/Notification/NotificationContext';
import { LoadingContext } from '../../../context/loadingContext/loadingContext';
import contextDefaultValues from '../../../context/bookingContext/bookingContext.const';

// interface
import {
  BookingDataEnum,
  BookingDataResponse,
  BookingDataStatus,
  BookingManagementEnum,
} from '../BookingManagment/bookingManagment.interface';
import mapToCreateQuoteResponse from '../BookingQuoteManagment/bookingQuoteMaper';
import {
  BookingCatalog,
  BookingConfigEnum,
  BookingStepValueEnum,
  BookingSubStepValueEnum,
} from '../bookingSteps.interface';
import { BookingStepsContextState } from '../../../context/bookingContext/bookingContext.types';
// util
import useApi from '../../../shared/services/api.service';
import getUrlSearchParam from '../../../shared/helpers/urlSearchParams.helper';
import useQuoteDataService from '../../../shared/services/quoteData.service';
import useBookingProductSelection from './useBookingProductSelection';
import {
  BookingQuoteResponseQuoteProduct,
  BookingQuoteResponseQuoteProductEnum,
} from '../../QuotationProposals/components/quotationProposal.interface';
import ErrorLoggingService from '../../../shared/services/errorlogging.service';
import axiosErrorToTranslationKey, {
  cm360ErrorCodes,
} from '../../../shared/helpers/axiosErrorToTranslationKey.helper';
import { useSessionContext } from '../../../context/sessionContext/sessionContext';
import { isDateBeforeNow } from '../../../shared/helpers/date.helper';
import { AppConfigurationContext } from '../../../context/appConfigurationContext/appConfigurationContext';
import { AppError } from '../../../context/appConfigurationContext/appConfigurationContext.types';
import { CacheDataContext } from '../../../context/cacheContext/cacheContext';

type UseBookingResume = {
  resumeBooking: () => void;
  showEmailVerification: boolean;
  setShowEmailVerification: (show: boolean) => void;
  handleEmailVerification: (email: string) => void;
};

type UseBookingResumeProps = {
  setResumedStep: (stepName: string) => void;
};

export const statusesNotEligibleForOperations: BookingDataStatus[] = [
  BookingDataStatus.DONE,
  BookingDataStatus.ABANDONED,
  BookingDataStatus.REISSUED,
];

const useBookingResume = ({
  setResumedStep,
}: UseBookingResumeProps): UseBookingResume => {
  // util
  const { t } = useTranslation();
  // context
  const { bookingConfigData } = useContext(BookingConfigContext);
  const { bookingSteps } = useContext(BookingContext);
  const { loadQuote, loadBooking, update } = useContext(BookingContext);
  const { bookingQuoteResponse, updateQuote } = useContext(BookingQuoteContext);
  const { updateBookingData } = useContext(BookingDataContext);
  const { showNotification } = useContext(NotificationContext);
  const { setIsLoading } = useContext(LoadingContext);
  const { setAppError } = useContext(AppConfigurationContext);
  const { getUrlParam } = useContext(CacheDataContext);
  // ref
  const latestBookingSteps = useRef<BookingStepsContextState>(
    contextDefaultValues.bookingSteps,
  );
  // service
  const { getQuote } = useQuoteDataService({
    bookingConfigData,
    bookingQuoteResponse,
  });
  // util
  const { sessionParams } = useSessionContext();
  const API = useApi(bookingConfigData, sessionParams);
  const { selectProduct } = useBookingProductSelection();
  const errorService: ErrorLoggingService = ErrorLoggingService.getInstance();
  // data
  const productCode: string | null = getUrlParam('productCode');
  const bookingType: string | null = getUrlParam('bookingType');
  let quoteId: string | null = getUrlParam('quoteId');
  const bookingDataId: string | null = getUrlParam('bookingDataId');
  const accessTokenParam: string | null = getUrlParam('token');
  const apiCM360Url: string =
    bookingConfigData[BookingConfigEnum.DataCenter]?.cm360Endpoint;
  // Add new state
  const [showEmailVerification, setShowEmailVerification] = useState(false);

  useEffect((): void => {
    latestBookingSteps.current = bookingSteps;
  }, [bookingSteps]);

  const handleLoadBookingError = useCallback(
    (e: unknown): AppError | null => {
      setIsLoading(false);

      if (e instanceof AxiosError && !!e.response) {
        if (e.response.status === 404) {
          showNotification('expiredLink', 'error', false);
          return {
            error: 'Failed to load booking',
            description: 'Booking not found',
            traceStack: e,
          };
        }
        if (
          e.response.status === 403 &&
          e.response.data.message === cm360ErrorCodes[403].cm01.code
        ) {
          showNotification('bookingAlreadyDone', 'error', false);
          return {
            error: 'Failed to load booking',
            description: t(cm360ErrorCodes[403].cm01.translationKey),
            traceStack: e,
          };
        }
        if (
          e.response.status === 403 &&
          e.response.data.message === cm360ErrorCodes[403].cm02.code
        ) {
          showNotification('accessDenied', 'error', false);
          return {
            error: 'Failed to load booking',
            description: t(cm360ErrorCodes[403].cm02.translationKey),
            traceStack: e,
          };
        }
      }
      if (e instanceof Error && e.message === 'configurationMismatch') {
        showNotification('configurationMismatch', 'error', false);
        return {
          error: 'Failed to load booking',
          description: 'Configuration mismatch',
          traceStack: e,
        };
      }
      if (e instanceof Error && e.message === 'bookingAlreadyDone') {
        showNotification('bookingAlreadyDone', 'error', false);
        return {
          error: 'Failed to load booking',
          description: 'Booking already done',
          traceStack: e,
        };
      }
      showNotification('unexpectedError', 'error', false);
      return {
        error: 'Failed to load booking',
        description: 'Unexpected error',
        traceStack: new Error(e as string),
      };
    },
    [setIsLoading, showNotification],
  );

  const getBooking = useCallback(
    async (
      id: string,
      email?: string,
    ): Promise<{
      bookingResponse: BookingDataResponse;
      quoteToken: string;
    } | void> => {
      try {
        let url = `${apiCM360Url}/booking/${id}`;
        const secret: string | null = getUrlParam('secret');
        const isHomeMedical: string | null = getUrlParam('homeMedical');
        const secretType = isHomeMedical ? 'medical' : 'payment';

        if (secret) {
          url = url.concat(
            `?secret=${secret}&email=${
              email ? encodeURIComponent(email) : ''
            }&secretType=${secretType}`,
          );
        }

        const bookingRes: AxiosResponse = await API.get(url);
        if (bookingRes.status !== 200) {
          showNotification('unexpectedError', 'error', false);
        }
        if (bookingRes.headers['x-amzn-remapped-authorization']) {
          localStorage.setItem(
            'token',
            bookingRes.headers['x-amzn-remapped-authorization'].replace(
              'Bearer ',
              '',
            ),
          );
        }
        const { catalog: bookingCatalog, status } =
          bookingRes.data?.[BookingManagementEnum.BookingData] || {};

        if (bookingCatalog) {
          const catalogExists = bookingConfigData[
            BookingConfigEnum.Catalogs
          ].filter(
            (catalog: BookingCatalog) => catalog.code === bookingCatalog,
          );
          if (catalogExists.length === 0) {
            throw new Error('configurationMismatch');
          }
        }

        if (statusesNotEligibleForOperations.includes(status)) {
          throw new Error('bookingAlreadyDone');
        }

        return {
          bookingResponse: bookingRes.data,
          quoteToken: bookingRes.headers['x-amzn-remapped-authorization'],
        };
      } catch (e) {
        const error = handleLoadBookingError(e);
        if (error) {
          setAppError(error);
          return Promise.resolve();
        }
        return Promise.reject(e);
      }
    },
    [API, apiCM360Url, bookingConfigData, handleLoadBookingError, setAppError],
  );

  const loadSavedData = useCallback(
    async (email?: string) => {
      setIsLoading(true);
      const id = quoteId || bookingDataId;
      const bookingResponseData = await getBooking(id!, email);
      if (bookingResponseData && bookingResponseData.bookingResponse) {
        const { bookingResponse, quoteToken } = bookingResponseData;

        if (
          bookingResponse[BookingManagementEnum.BookingData][
            BookingDataEnum.PersistentQuoteId
          ]
        ) {
          update(
            bookingResponse[BookingManagementEnum.BookingData][
              BookingDataEnum.PersistentQuoteId
            ],
            BookingStepValueEnum.ExtraFields,
            BookingSubStepValueEnum.InitialQuoteId,
          );
        }

        if (
          bookingResponse[BookingManagementEnum.BookingData][
            BookingDataEnum.CurrentStep
          ] === 'emptyBooking'
        ) {
          if (productCode) {
            const response: BookingQuoteResponseQuoteProduct =
              await selectProduct(id!, productCode, true);
            quoteId =
              response[BookingQuoteResponseQuoteProductEnum.PSResponsePayload]
                .quoteId;
            update(
              {
                ...bookingSteps[BookingStepValueEnum.QuotationProposals],
                proposal:
                  response[BookingQuoteResponseQuoteProductEnum.Product],
              },
              BookingStepValueEnum.QuotationProposals,
            );
          }
          bookingResponse[BookingManagementEnum.BookingData][
            BookingDataEnum.CurrentStep
          ] = 'quotationProposals';
        }
        updateBookingData(bookingResponse);
        const latestQuoteId =
          bookingResponse[BookingManagementEnum.BookingData][
            BookingDataEnum.Proxy
          ]?.quoteId ?? quoteId;
        if (latestQuoteId) {
          let quote;

          try {
            quote = await getQuote({
              id: latestQuoteId,
              bookingResponse,
              quoteToken,
            });
          } catch (error: any) {
            if (axios.isAxiosError(error)) {
              const translationKey = axiosErrorToTranslationKey(error);
              showNotification(translationKey, 'error', false);
            } else {
              showNotification(error.message, 'error', true);
            }
            setIsLoading(false);
          }

          if (!quote) return;

          const { quoteResult, medicalConditionsResult } = quote;

          if (
            bookingResponse[BookingManagementEnum.Companions]?.length !==
            quoteResult.beneficiaries.length
          ) {
            errorService.log(
              `[useBookingResume] Mismatch: Companions count (${bookingResponse[
                BookingManagementEnum.Companions
              ]?.length}) does not match beneficiaries count (${
                quoteResult.beneficiaries.length
              })`,
            );
          }
          updateQuote(
            mapToCreateQuoteResponse(
              latestQuoteId,
              bookingResponse[BookingManagementEnum.BookingData][
                BookingDataEnum.Proxy
              ]?.policyId ?? '',
              quoteResult,
              accessTokenParam,
            ),
          );

          try {
            loadQuote({
              quote: quoteResult,
              medicalConditions: medicalConditionsResult,
              consents: bookingResponse.consents,
              bookingResponse,
              latestBookingSteps: latestBookingSteps.current,
            });
          } catch (error: any) {
            errorService.log('[useBookingResume] Error loading quote:', error);
            showNotification('unexpectedError', 'error', false);
            setIsLoading(false);
            return;
          }
        } else {
          try {
            loadBooking(bookingResponse);
          } catch (error) {
            errorService.log(
              '[useBookingResume] Error loading booking:',
              error,
            );
            showNotification('unexpectedError', 'error', false);
            setIsLoading(false);
            if (error instanceof Error) {
              setAppError({
                error: 'Failed to load booking',
                description: error.message,
                traceStack: error,
              });
            }
            return;
          }
        }
        if (getUrlParam('paymentHome') !== 'true') {
          setResumedStep(bookingResponse.bookingData.currentStep);
        }
        setIsLoading(false);
      }
    },
    [
      accessTokenParam,
      bookingDataId,
      getBooking,
      getQuote,
      loadBooking,
      loadQuote,
      quoteId,
      setIsLoading,
      setResumedStep,
      updateBookingData,
      updateQuote,
    ],
  );

  const handleEmailVerificationClose = useCallback(() => {
    setShowEmailVerification(false);
    setAppError({
      error: 'Email Required',
      description: 'Email is required for this booking',
      traceStack: new Error('Email verification cancelled'),
    });
  }, [setAppError]);

  const handleEmailVerification = (email: string): void => {
    setShowEmailVerification(false);
    loadSavedData(email);
  };

  const resumeBooking = useCallback(() => {
    if (bookingConfigData.bookingSteps.length) {
      if (quoteId || bookingDataId) {
        const secret: string | null = getUrlParam('secret');
        if (secret) {
          setShowEmailVerification(true);
        } else {
          loadSavedData();
        }
      } else {
        // If no quoteId or bookingId, just set the resumed step
        setResumedStep(bookingSteps[BookingStepValueEnum.CurrentStep]);
      }
    }
  }, [
    bookingConfigData.bookingSteps.length,
    bookingDataId,
    bookingSteps,
    quoteId,
    setResumedStep,
  ]);

  return {
    resumeBooking,
    showEmailVerification,
    setShowEmailVerification: handleEmailVerificationClose,
    handleEmailVerification,
  };
};

export default useBookingResume;
